import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaHourglassHalf,
  FaCheckCircle,
  FaPlay,
  FaFlagCheckered,
  FaMapMarkerAlt,
  FaCar,
  FaCalendarAlt,
  FaBan,
  FaRupeeSign,
  FaCoins,
  FaPercentage,
  FaBalanceScale,
  FaStar,
  FaSync,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import {
  COMPARISON_MODES,
  calcPercentChange,
  computeComparisonRange,
  formatBreakdownDateRangeLabel,
  formatSignedDiff,
} from "../utils/rideBreakdownComparison";

const emptyStats = {
  totalUsers: 0,
  totalRides: 0,
  requested: 0,
  accepted: 0,
  arrived: 0,
  started: 0,
  completed: 0,
  planned: 0,
  cancelled: 0,
  totalRidesFare: 0,
  requestedFare: 0,
  acceptedFare: 0,
  arrivedFare: 0,
  startedFare: 0,
  completedFare: 0,
  plannedFare: 0,
  cancelledFare: 0,
  totalRidesCommission: 0,
  requestedCommission: 0,
  acceptedCommission: 0,
  arrivedCommission: 0,
  startedCommission: 0,
  completedCommission: 0,
  plannedCommission: 0,
  cancelledCommission: 0,
  // Kept (still returned by backend) in case the breakdown cards are re-enabled.
  userCancelled: 0,
  noDriversTimeout: 0,
  driverUnavailable: 0,
  revenue: 0,
  driverEarnings: 0,
  totalCommission: 0,
  commissionPaid: 0,
  commissionPending: 0,
  totalGst: 0,
  gstPaid: 0,
  gstPending: 0,
  totalDue: 0,
  duePaid: 0,
  duePending: 0,
  averageCustomerRating: 0,
  averageDriverRating: 0,
};

const row1 = [
  {
    key: "totalRides",
    label: "Total Rides",
    gradient: "bg-gradient-to-r from-slate-600 to-slate-700",
    icon: FaCar,
  },
  {
    key: "requested",
    label: "Requested",
    gradient: "bg-gradient-to-r from-amber-500 to-amber-600",
    icon: FaHourglassHalf,
  },
  {
    key: "accepted",
    label: "Accepted",
    gradient: "bg-gradient-to-r from-blue-500 to-blue-600",
    icon: FaCheckCircle,
  },
  {
    key: "arrived",
    label: "Arrived",
    gradient: "bg-gradient-to-r from-teal-500 to-teal-600",
    icon: FaMapMarkerAlt,
  },
];

const row2 = [
  {
    key: "started",
    label: "Started",
    gradient: "bg-gradient-to-r from-cyan-500 to-cyan-600",
    icon: FaPlay,
  },
  {
    key: "completed",
    label: "Completed",
    gradient: "bg-gradient-to-r from-green-500 to-green-600",
    icon: FaFlagCheckered,
  },
  {
    key: "cancelled",
    label: "Cancelled",
    gradient: "bg-gradient-to-r from-gray-500 to-gray-600",
    icon: FaBan,
  },
  /*
   * Detailed cancellation breakdown cards — temporarily disabled in favour of a
   * single overall "Cancelled" card above. Backend still returns these fields,
   * so re-enabling is a copy-paste away.
   *
   * {
   *   key: "userCancelled",
   *   label: "User cancelled ride",
   *   gradient: "bg-gradient-to-r from-rose-500 to-rose-600",
   *   icon: FaUserSlash,
   * },
   * {
   *   key: "noDriversTimeout",
   *   label: "No drivers available - request timed out",
   *   gradient: "bg-gradient-to-r from-orange-500 to-orange-600",
   *   icon: FaClock,
   * },
   * {
   *   key: "driverUnavailable",
   *   label: "Driver unavailable",
   *   gradient: "bg-gradient-to-r from-red-500 to-red-600",
   *   icon: FaCar,
   * },
   */
  {
    key: "planned",
    label: "Planned",
    gradient: "bg-gradient-to-r from-indigo-500 to-indigo-600",
    icon: FaCalendarAlt,
  },
];

const cardBaseClass =
  "rounded-xl p-4 shadow-md text-white transition-transform duration-150";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

const formatMoney = (value) => currencyFormatter.format(Number(value) || 0);

const formatStatusSharePercent = (count, total) => {
  const c = Number(count) || 0;
  const t = Number(total) || 0;
  if (c <= 0 || t <= 0) return "0";

  const pct = (c / t) * 100;
  if (pct < 1) {
    return pct.toFixed(2);
  }
  return String(Math.round(pct));
};

const formatRating = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "0.0";
  return numeric.toFixed(1);
};

const topSummaryCards = [
  {
    key: "revenue",
    label: "Revenue",
    icon: FaRupeeSign,
    gradient: "bg-gradient-to-r from-sky-600 to-blue-700",
  },
  {
    key: "driverEarnings",
    label: "Driver Earnings",
    icon: FaCoins,
    gradient: "bg-gradient-to-r from-emerald-600 to-green-700",
  },
];

const detailedSummaryCards = [
  {
    key: "totalCommission",
    label: "Total Commission",
    icon: FaPercentage,
    gradient: "bg-gradient-to-r from-indigo-600 to-violet-700",
    subItems: [
      { label: "Commission Paid", key: "commissionPaid" },
      { label: "Commission Pending", key: "commissionPending" },
    ],
  },
  {
    key: "totalGst",
    label: "Total GST",
    icon: FaRupeeSign,
    gradient: "bg-gradient-to-r from-orange-500 to-amber-600",
    subItems: [
      { label: "GST Paid", key: "gstPaid" },
      { label: "GST Pending", key: "gstPending" },
    ],
  },
  {
    key: "totalDue",
    label: "Total Due",
    icon: FaBalanceScale,
    gradient: "bg-gradient-to-r from-rose-500 to-red-600",
    subItems: [
      { label: "Due Paid", key: "duePaid" },
      { label: "Due Pending", key: "duePending" },
    ],
  },
  {
    key: "averageRating",
    label: "Average Rating",
    icon: FaStar,
    gradient: "bg-gradient-to-r from-cyan-600 to-teal-700",
    subItems: [
      { label: "Customer", key: "averageCustomerRating", isRating: true },
      { label: "Driver", key: "averageDriverRating", isRating: true },
    ],
  },
];

const summaryCards = [...topSummaryCards, ...detailedSummaryCards];

const summaryFilterByKey = {
  revenue: { status: "Completed", dueStatus: "", ratingType: "" },
  driverEarnings: { status: "Completed", dueStatus: "", ratingType: "" },
  totalCommission: { status: "Completed", dueStatus: "", ratingType: "" },
  totalGst: { status: "Completed", dueStatus: "", ratingType: "" },
  totalDue: { status: "Completed", dueStatus: "", ratingType: "" },
  averageRating: { status: "Completed", dueStatus: "", ratingType: "any" },
};

const summarySubFilterByKey = {
  commissionPaid: { status: "Completed", dueStatus: "Paid", ratingType: "" },
  commissionPending: { status: "Completed", dueStatus: "Pending", ratingType: "" },
  gstPaid: { status: "Completed", dueStatus: "Paid", ratingType: "" },
  gstPending: { status: "Completed", dueStatus: "Pending", ratingType: "" },
  duePaid: { status: "Completed", dueStatus: "Paid", ratingType: "" },
  duePending: { status: "Completed", dueStatus: "Pending", ratingType: "" },
  // Backend ratingType indicates who gave the rating:
  // "customer" => customer rated driver, "driver" => driver rated customer.
  // UI labels indicate who received the rating, so these are intentionally inverted.
  averageCustomerRating: {
    status: "Completed",
    dueStatus: "",
    ratingType: "driver",
  },
  averageDriverRating: {
    status: "Completed",
    dueStatus: "",
    ratingType: "customer",
  },
};

const statusByKey = {
  totalRides: "",
  requested: "Requested",
  accepted: "Accepted",
  started: "Started",
  arrived: "Arrived",
  completed: "Completed",
  cancelled: "Cancelled",
  planned: "Planned",
};

const fareKeyByCardKey = {
  totalRides: "totalRidesFare",
  requested: "requestedFare",
  accepted: "acceptedFare",
  arrived: "arrivedFare",
  started: "startedFare",
  completed: "completedFare",
  cancelled: "cancelledFare",
  planned: "plannedFare",
};

const commissionKeyByCardKey = {
  totalRides: "totalRidesCommission",
  requested: "requestedCommission",
  accepted: "acceptedCommission",
  arrived: "arrivedCommission",
  started: "startedCommission",
  completed: "completedCommission",
  cancelled: "cancelledCommission",
  planned: "plannedCommission",
};

// Skip pagination/sort/visualisation-only keys when forwarding filters to the
// backend; keep parity with fetchRides serialization in pages/Rides.js.
const SKIP_FILTER_KEYS = new Set([
  "page",
  "limit",
  "sortBy",
  "sortOrder",
  // Keep status cards meaningful after selecting a status card:
  // rides list gets filtered by status, but breakdown remains across all statuses.
  "status",
  // Assigned-employee card clicks can set scheduleType; keep this panel unsliced.
  "scheduleType",
  // Keep summary-card clicks meaningful like top status cards.
  "dueStatus",
  "ratingType",
]);

const buildQueryString = (filters) => {
  const params = new URLSearchParams();
  if (!filters || typeof filters !== "object") return params.toString();
  Object.entries(filters).forEach(([key, value]) => {
    if (
      SKIP_FILTER_KEYS.has(key) ||
      value === undefined ||
      value === null ||
      typeof value !== "string"
    ) {
      return;
    }
    const trimmed = value.trim();
    if (trimmed) params.append(key, trimmed);
  });
  return params.toString();
};

const buildComparisonBreakdownUrl = (baseQueryString, startDate, endDate) => {
  const params = new URLSearchParams(baseQueryString || "");
  params.set("startDate", startDate);
  params.set("endDate", endDate);
  return `${API.ADMIN.RIDES_STATUS_BREAKDOWN}?${params.toString()}`;
};

const Card = ({
  cfg,
  value,
  fareValue,
  commissionValue,
  totalRidesCount,
  usersSharePercent = null,
  loading,
  onClick,
  isActive,
  comparison,
  hideMetrics = false,
  hideShare = false,
}) => {
  const Icon = cfg.icon;
  const nextStatus = statusByKey[cfg.key];
  const isTotalRidesCard = cfg.key === "totalRides";
  const showRideStatusShare = !hideShare && !isTotalRidesCard;
  const showUsersShare =
    isTotalRidesCard &&
    usersSharePercent !== null &&
    usersSharePercent !== undefined;
  const comparisonTrendClass = (direction) =>
    direction === "down"
      ? "text-red-200"
      : direction === "flat"
        ? "text-white/80"
        : "text-emerald-200";
  const canClick =
    typeof onClick === "function" &&
    Object.prototype.hasOwnProperty.call(statusByKey, cfg.key);
  const classes = `${cardBaseClass} ${cfg.gradient} ${
    canClick
      ? "cursor-pointer hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/70"
      : ""
  } ${isActive ? "ring-4 ring-offset-2 ring-offset-white ring-slate-800 scale-[1.02]" : ""}`;

  const handleCardClick = () => {
    if (!canClick) return;
    onClick(nextStatus);
  };

  const displayValue =
    typeof value === "number" || typeof value === "string"
      ? Number(value ?? 0).toLocaleString("en-IN")
      : value ?? 0;

  const inner = (
    <>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider opacity-90 truncate pr-2">
          {cfg.label}
        </span>
        <Icon className="text-lg opacity-80 flex-shrink-0" />
      </div>
      <div className="text-3xl font-bold leading-tight">
        {loading ? (
          <span className="inline-block h-8 w-20 rounded bg-white/30 animate-pulse" />
        ) : (
          <>
            {displayValue}
            {showUsersShare && (
              <span className="text-xl font-semibold opacity-90 ml-1">
                ({usersSharePercent}%)
              </span>
            )}
            {showRideStatusShare && (
              <span className="text-xl font-semibold opacity-90 ml-1">
                ({formatStatusSharePercent(value, totalRidesCount)}%)
              </span>
            )}
          </>
        )}
      </div>
      {!hideMetrics && (
        <div className="mt-2 pt-2 border-t border-white/30">
          <div className="text-base font-semibold opacity-95">
            {loading ? (
              <span className="inline-block h-5 w-24 rounded bg-white/30 animate-pulse" />
            ) : (
              <>
                {formatMoney(fareValue)}
                <span className="text-sm font-medium opacity-90 ml-1">
                  ({formatMoney(commissionValue)})
                </span>
              </>
            )}
          </div>
        </div>
      )}
      {comparison && !loading && (
        <div className="mt-3 pt-3 border-t border-dashed border-white/40">
          <div className="flex items-end justify-between gap-2 text-sm">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wide opacity-75 mb-0.5">vs</div>
              <div className="font-semibold leading-tight">
                {Number(comparison.count ?? 0).toLocaleString("en-IN")}
              </div>
              <div className="text-xs opacity-90 truncate">{formatMoney(comparison.fare)}</div>
            </div>
            <div className="text-right min-w-0">
              <div
                className={`flex items-center justify-end gap-1 font-semibold whitespace-nowrap ${comparisonTrendClass(
                  comparison.percentChange.direction
                )}`}
              >
                <span>
                  {formatSignedDiff(value ?? 0, comparison.count ?? 0, (v) =>
                    Number(v).toLocaleString("en-IN")
                  )}
                </span>
                {comparison.percentChange.direction === "down" ? (
                  <FaArrowDown className="text-xs shrink-0" />
                ) : comparison.percentChange.direction === "up" ? (
                  <FaArrowUp className="text-xs shrink-0" />
                ) : null}
                <span>{comparison.percentChange.display}</span>
              </div>
              <div
                className={`text-xs font-semibold truncate mt-0.5 ${comparisonTrendClass(
                  comparison.farePercentChange?.direction ||
                    comparison.percentChange.direction
                )}`}
              >
                {formatSignedDiff(fareValue ?? 0, comparison.fare ?? 0, formatMoney)}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <button
      type="button"
      className={classes}
      title={cfg.label}
      onClick={handleCardClick}
      disabled={!canClick}
      aria-label={`Filter rides by ${cfg.label}`}
    >
      {inner}
    </button>
  );
};

const SummaryCard = ({
  cfg,
  stats,
  loading,
  compact = false,
  onCardClick,
  onSubCardClick,
  isCardActive,
  activeSubKeys = [],
}) => {
  const Icon = cfg.icon;
  const hasSubCards = Array.isArray(cfg.subItems) && cfg.subItems.length > 0;
  const cardClickable = typeof onCardClick === "function";

  if (hasSubCards) {
    const moneySubItems = (cfg.subItems || []).filter((item) => !item.isRating);
    const subTotal = moneySubItems.reduce(
      (sum, item) => sum + (Number(stats[item.key]) || 0),
      0,
    );
    const showInlineTotal = moneySubItems.length > 0;

    return (
      <button
        type="button"
        onClick={cardClickable ? onCardClick : undefined}
        disabled={!cardClickable}
        className={`${cardBaseClass} ${cfg.gradient} text-left w-full ${
          cardClickable
            ? "cursor-pointer hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/70"
            : ""
        } ${isCardActive ? "ring-4 ring-offset-2 ring-offset-white ring-slate-800 scale-[1.02]" : ""}`}
        aria-label={`Filter rides by ${cfg.label}`}
      >
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-xs uppercase tracking-wider text-white/95 truncate">
              {cfg.label}
            </span>
            {showInlineTotal && (
              <span className="text-sm font-semibold text-white whitespace-nowrap">
                {loading ? (
                  <span className="inline-block h-4 w-14 rounded bg-white/30 animate-pulse" />
                ) : (
                 <span className="text-white/90">({ formatMoney(subTotal) })</span>
                )}
              </span>
            )}
          </div>
          <span className="h-6 w-6 rounded-full bg-white/20 border border-white/30 inline-flex items-center justify-center flex-shrink-0">
            <Icon className="text-xs text-white" />
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          {cfg.subItems.map((item) => (
            <button
              type="button"
              key={item.key}
              onClick={(event) => {
                event.stopPropagation();
                if (typeof onSubCardClick === "function") onSubCardClick(item.key);
              }}
              className={`rounded-lg bg-white/14 border border-white/20 px-2 py-2 text-center transition-all duration-150 ${
                typeof onSubCardClick === "function"
                  ? "cursor-pointer hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
                  : ""
              } ${activeSubKeys.includes(item.key) ? "ring-2 ring-white/80 bg-white/20" : ""}`}
            >
              <p className="text-[11px] leading-4 text-white/90 truncate">{item.label}</p>
              <p className="text-2xl font-bold leading-tight mt-1 text-white">
                {loading
                  ? <span className="inline-block h-7 w-16 rounded bg-white/30 animate-pulse" />
                  : item.isRating
                    ? `${formatRating(stats[item.key])}`
                    : formatMoney(stats[item.key])}
              </p>
            </button>
          ))}
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={cardClickable ? onCardClick : undefined}
      disabled={!cardClickable}
      className={`${cardBaseClass} ${cfg.gradient} text-left w-full ${
        cardClickable
          ? "cursor-pointer hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/70"
          : ""
      } ${isCardActive ? "ring-4 ring-offset-2 ring-offset-white ring-slate-800 scale-[1.02]" : ""}`}
      aria-label={`Filter rides by ${cfg.label}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider opacity-90 truncate pr-2">
          {cfg.label}
        </span>
        <Icon className="text-lg opacity-80 flex-shrink-0" />
      </div>

      <div className={`${compact ? "text-2xl" : "text-3xl"} font-bold leading-tight`}>
        {loading ? (
          <span className="inline-block h-8 w-24 rounded bg-white/30 animate-pulse" />
        ) : (
          formatMoney(stats[cfg.key])
        )}
      </div>
    </button>
  );
};

const SEARCH_FILTER_LABELS = {
  search: "Search",
  customerSearch: "Customer",
  driverSearch: "Driver",
  city: "City",
  stateUt: "State/UT",
  rideId: "Ride ID",
  estimatedPickupLocation: "Pickup",
  fare: "Fare",
  tripType: "Trip Type",
  paymentStatus: "Payment Status",
  paymentMethod: "Payment Method",
  assignedEmployeeId: "Assigned Employee",
};

const getActiveSearchFilterLabels = (filters) => {
  if (!filters || typeof filters !== "object") return [];
  return Object.entries(SEARCH_FILTER_LABELS)
    .filter(([key]) => {
      const value = filters[key];
      return typeof value === "string" && value.trim();
    })
    .map(([, label]) => label);
};

const RidesStatusStatsPanel = ({
  filters,
  onStatusClick,
  onSummaryFilter,
}) => {
  const [stats, setStats] = useState(emptyStats);
  const [platformUserCount, setPlatformUserCount] = useState(null);
  const [comparisonStats, setComparisonStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSummarySelection, setActiveSummarySelection] = useState(null);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareMode, setCompareMode] = useState("previous_period");
  const [compareStartDate, setCompareStartDate] = useState("");
  const [compareEndDate, setCompareEndDate] = useState("");
  const [appliedComparison, setAppliedComparison] = useState(null);
  const abortRef = useRef(null);

  const queryString = useMemo(() => buildQueryString(filters), [filters]);
  const selectedStatus = (filters?.status || "").trim().toLowerCase();
  const mainStartDate = (filters?.startDate || "").trim();
  const mainEndDate = (filters?.endDate || "").trim();
  const hasMainDateRange = Boolean(mainStartDate && mainEndDate);
  const isCustomCompare = compareMode === "custom";
  const activeSearchFilters = useMemo(
    () => getActiveSearchFilterLabels(filters),
    [filters]
  );

  const applySummaryFilter = (preset, selection) => {
    if (typeof onSummaryFilter !== "function" || !preset) return;
    setActiveSummarySelection(selection || null);
    onSummaryFilter(preset);
  };
  const totalRidesCount =
    Number(stats.totalRides) ||
    [
      "requested",
      "accepted",
      "arrived",
      "started",
      "completed",
      "planned",
      "cancelled",
    ].reduce((sum, key) => sum + Number(stats[key] || 0), 0);

  const totalUsersCount =
    platformUserCount !== null && platformUserCount !== undefined
      ? Number(platformUserCount) || 0
      : Number(stats.totalUsers) || 0;

  const ridesToUsersSharePercent =
    totalUsersCount > 0
      ? formatStatusSharePercent(totalRidesCount, totalUsersCount)
      : null;

  const comparisonTotalRidesCount = comparisonStats
    ? Number(comparisonStats.totalRides) ||
      [
        "requested",
        "accepted",
        "arrived",
        "started",
        "completed",
        "planned",
        "cancelled",
      ].reduce((sum, key) => sum + Number(comparisonStats[key] || 0), 0)
    : 0;

  useEffect(() => {
    if (!compareEnabled || compareMode === "none") return;
    const preview = computeComparisonRange(
      compareMode,
      mainStartDate,
      mainEndDate,
      compareStartDate,
      compareEndDate
    );
    if (preview && compareMode !== "custom") {
      setCompareStartDate(preview.startDate);
      setCompareEndDate(preview.endDate);
    }
  }, [compareEnabled, compareMode, mainStartDate, mainEndDate]);

  const handleCompareToggle = (enabled) => {
    setCompareEnabled(enabled);
    if (!enabled) {
      setAppliedComparison(null);
      setComparisonStats(null);
      setCompareMode("previous_period");
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);

      const mainUrl = queryString
        ? `${API.ADMIN.RIDES_STATUS_BREAKDOWN}?${queryString}`
        : API.ADMIN.RIDES_STATUS_BREAKDOWN;
      const mainRequest = axiosInstance.get(mainUrl, {
        signal: controller.signal,
      });

      // Backend status-breakdown has no compareMode; load the prior range as a
      // second request (same pattern as Drivers / CommissionReport).
      const comparisonRange =
        appliedComparison?.startDate && appliedComparison?.endDate
          ? appliedComparison
          : null;
      const comparisonRequest = comparisonRange
        ? axiosInstance.get(
            buildComparisonBreakdownUrl(
              queryString,
              comparisonRange.startDate,
              comparisonRange.endDate
            ),
            { signal: controller.signal }
          )
        : Promise.resolve(null);

      Promise.all([mainRequest, comparisonRequest])
        .then(async ([res, compareRes]) => {
          const data = res.data?.data || {};
          setStats({ ...emptyStats, ...data });

          const breakdownHasTotalUsers = Object.prototype.hasOwnProperty.call(
            data,
            "totalUsers"
          );
          const breakdownUserCount = breakdownHasTotalUsers
            ? Number(data.totalUsers) || 0
            : null;

          let dashboardUserCount = null;
          try {
            const dashboardRes = await axiosInstance.get(
              API.ADMIN.DASHBOARD_STATS,
              { signal: controller.signal }
            );
            const registeredUsers = Number(
              dashboardRes.data?.stats?.registeredUsers
            );
            const totalUsers = Number(dashboardRes.data?.stats?.totalUsers);
            if (Number.isFinite(registeredUsers) && registeredUsers >= 0) {
              dashboardUserCount = registeredUsers;
            } else if (Number.isFinite(totalUsers) && totalUsers >= 0) {
              dashboardUserCount = totalUsers;
            }
          } catch {
            // Dashboard fallback is optional; breakdown count still applies.
          }

          if (breakdownUserCount !== null && breakdownUserCount > 0) {
            setPlatformUserCount(breakdownUserCount);
          } else if (dashboardUserCount !== null && dashboardUserCount > 0) {
            setPlatformUserCount(dashboardUserCount);
          } else if (breakdownUserCount !== null) {
            setPlatformUserCount(breakdownUserCount);
          } else if (dashboardUserCount !== null) {
            setPlatformUserCount(dashboardUserCount);
          } else {
            setPlatformUserCount(0);
          }

          const compareData = compareRes?.data?.data;
          if (compareData) {
            setComparisonStats({ ...emptyStats, ...compareData });
          } else {
            setComparisonStats(null);
          }
        })
        .catch((err) => {
          if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
          const message =
            err.response?.data?.message || "Failed to load status breakdown";
          setError(message);
        })
        .finally(() => {
          if (abortRef.current === controller) {
            setLoading(false);
          }
        });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [queryString, appliedComparison]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handleApplyComparison = () => {
    if (!compareEnabled) return;
    const range = computeComparisonRange(
      compareMode,
      mainStartDate,
      mainEndDate,
      compareStartDate,
      compareEndDate
    );
    if (!range) return;
    setAppliedComparison(range);
  };

  const handleClearComparison = () => {
    setAppliedComparison(null);
    setComparisonStats(null);
    setCompareMode("previous_period");
  };

  const canApplyComparison =
    compareEnabled &&
    compareMode !== "none" &&
    (compareMode === "custom"
      ? Boolean(compareStartDate && compareEndDate)
      : hasMainDateRange);

  const buildCardComparison = (cfg) => {
    if (!comparisonStats || !appliedComparison) return null;
    const countKey = cfg.key;
    const fareKey = fareKeyByCardKey[cfg.key];
    const currentCount =
      countKey === "totalRides" ? totalRidesCount : Number(stats[countKey]) || 0;
    const previousCount =
      countKey === "totalRides"
        ? comparisonTotalRidesCount
        : Number(comparisonStats[countKey]) || 0;
    const currentFare = Number(stats[fareKey]) || 0;
    const previousFare = Number(comparisonStats[fareKey]) || 0;
    return {
      count: previousCount,
      fare: previousFare,
      percentChange: calcPercentChange(currentCount, previousCount),
      farePercentChange: calcPercentChange(currentFare, previousFare),
    };
  };

  const headerRangeLabel = hasMainDateRange
    ? formatBreakdownDateRangeLabel(mainStartDate, mainEndDate)
    : "";
  const appliedComparisonLabel = appliedComparison
    ? formatBreakdownDateRangeLabel(
        appliedComparison.startDate,
        appliedComparison.endDate
      )
    : "";

  return (
    <div className="bg-white rounded-lg shadow mb-6 p-4">
      <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Ride Reports
            </h4>
            <p className="mt-1 text-xs text-gray-500">
              Stats reflect the search filters and date range above.
              {activeSearchFilters.length > 0 && (
                <span className="ml-1">
                  Active: {activeSearchFilters.join(", ")}
                  {hasMainDateRange ? `, ${headerRangeLabel}` : ""}
                </span>
              )}
              {activeSearchFilters.length === 0 && hasMainDateRange && (
                <span className="ml-1">Date range: {headerRangeLabel}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Compare</span>
            <button
              type="button"
              role="switch"
              aria-checked={compareEnabled}
              onClick={() => handleCompareToggle(!compareEnabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                compareEnabled ? "bg-blue-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  compareEnabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>
        </div>

        {compareEnabled && (
          <div className="pt-3 border-t border-gray-200 flex flex-wrap items-end gap-3">
            <div className="min-w-[180px]">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                Compare With
              </label>
              <select
                value={compareMode}
                onChange={(e) => setCompareMode(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                {COMPARISON_MODES.filter((mode) => mode.value !== "none").map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[160px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Compare Start Date
              </label>
              <input
                type="date"
                value={compareStartDate}
                onChange={(e) => setCompareStartDate(e.target.value)}
                readOnly={!isCustomCompare}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 read-only:bg-gray-50"
              />
            </div>
            <div className="min-w-[160px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Compare End Date
              </label>
              <input
                type="date"
                value={compareEndDate}
                onChange={(e) => setCompareEndDate(e.target.value)}
                readOnly={!isCustomCompare}
                min={isCustomCompare ? compareStartDate || undefined : undefined}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 read-only:bg-gray-50"
              />
            </div>
            <button
              type="button"
              onClick={handleApplyComparison}
              disabled={!canApplyComparison || loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaSync className={loading ? "animate-spin" : ""} />
              Apply Comparison
            </button>
            {appliedComparison && (
              <button
                type="button"
                onClick={handleClearComparison}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {compareEnabled && !hasMainDateRange && compareMode !== "custom" && (
          <p className="mt-2 text-xs text-amber-700">
            Set Start Date and End Date to enable comparison.
          </p>
        )}
      </div>

      <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Total Users
            </h4>
            <p className="mt-1 text-xs text-gray-500">
              All registered users on the platform (excluding test records).
            </p>
          </div>
          <div className="text-right">
            {loading ? (
              <span className="inline-block h-8 w-24 rounded bg-gray-200 animate-pulse" />
            ) : (
              <p className="text-3xl font-bold text-gray-800">
                {totalUsersCount.toLocaleString("en-IN")}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
          Ride Status Breakdown
          {headerRangeLabel ? ` (${headerRangeLabel})` : ""}
        </h3>
        <div className="text-right">
          {appliedComparison && (
            <p className="text-xs text-gray-500">
              vs {appliedComparison.label} ({appliedComparisonLabel})
            </p>
          )}
          {error && (
            <span className="text-xs text-red-500 block" role="alert">
              {error}
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {row1.map((cfg) => (
          <Card
            key={cfg.key}
            cfg={cfg}
            value={cfg.key === "totalRides" ? totalRidesCount : stats[cfg.key]}
            fareValue={stats[fareKeyByCardKey[cfg.key]] ?? 0}
            commissionValue={stats[commissionKeyByCardKey[cfg.key]] ?? 0}
            totalRidesCount={totalRidesCount}
            usersSharePercent={
              cfg.key === "totalRides" ? ridesToUsersSharePercent : null
            }
            loading={loading}
            onClick={onStatusClick}
            isActive={statusByKey[cfg.key]?.toLowerCase() === selectedStatus}
            comparison={buildCardComparison(cfg)}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {row2.map((cfg) => (
          <Card
            key={cfg.key}
            cfg={cfg}
            value={stats[cfg.key]}
            fareValue={stats[fareKeyByCardKey[cfg.key]] ?? 0}
            commissionValue={stats[commissionKeyByCardKey[cfg.key]] ?? 0}
            totalRidesCount={totalRidesCount}
            loading={loading}
            onClick={onStatusClick}
            isActive={statusByKey[cfg.key]?.toLowerCase() === selectedStatus}
            comparison={buildCardComparison(cfg)}
          />
        ))}
      </div>
      {appliedComparison && (
        <p className="mt-4 text-xs text-gray-500">
          Note: Differences and percentages are based on the comparison with the{" "}
          {appliedComparison.label}.
        </p>
      )}

      <div className="mt-5 pt-5 border-t border-gray-100">
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">
          Revenue and Rating Breakdown
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {summaryCards.map((cfg) => (
            <SummaryCard
              key={cfg.key}
              cfg={cfg}
              stats={stats}
              loading={loading}
              compact={Boolean(cfg.subItems?.length)}
              onCardClick={() =>
                applySummaryFilter(summaryFilterByKey[cfg.key], {
                  kind: "card",
                  key: cfg.key,
                })
              }
              onSubCardClick={(subKey) =>
                applySummaryFilter(
                  summarySubFilterByKey[subKey] || summaryFilterByKey[cfg.key],
                  {
                    kind: "sub",
                    key: subKey,
                  }
                )
              }
              isCardActive={
                activeSummarySelection?.kind === "card" &&
                activeSummarySelection?.key === cfg.key
              }
              activeSubKeys={(cfg.subItems || [])
                .map((item) => item.key)
                .filter(
                  (key) =>
                    activeSummarySelection?.kind === "sub" &&
                    activeSummarySelection?.key === key
                )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RidesStatusStatsPanel;
